import { Restaurant } from '@maverick/entity';
import { GetStaticPropsResult } from 'next';
import { ThemeProvider } from 'styled-components';
import { OutbackTheme } from '@maverick/themes';
import { BrowsableMenu } from '@maverick/cms';
import { useRouter } from 'next/router';
import {
    BrowsableMenuPageProps,
    buildCategoryMetaDescription,
    getEmptyNavigationItems,
    GetFooter,
    GetHeader,
    retrieveRestaurantMenu,
    retrieveRestaurantWithCalendar,
    retrieveRestaurantBySlug,
    REVALIDATE_DELAY,
} from 'src/utils/Utils';
import Head from 'next/head';
import { dictionaryServiceFactory } from 'lib/dictionary-service-factory';
import { Config } from 'src/Config';
import { CreateIsoDate, getMenuCategoryTitle, buildRestaurantMenuJsonLdForSlug } from '@maverick/utils';
import { GetNavigationItems } from '@maverick/sitecore';
import { ToastProvider } from '@maverick/ui';

export const getStaticPaths = async () => {
    return {
        paths: [],
        fallback: 'blocking',
    };
};

export async function getStaticProps(
    context: BrowsableMenuPageProps
): Promise<GetStaticPropsResult<BrowsableMenuPageProps>> {
    const genericRestaurantId = String(process.env.GHOST_RESTAURANT_ID);
    const params = context.params;

    try {
        let restaurantMenu = null;
        let restaurantInfo = null;
        const dictionaryService = dictionaryServiceFactory.create(Config.Brand);
        const dictionaryPromise = dictionaryService.fetchDictionaryData('en');

        if (params.restaurantSlug !== 'restaurant') {
            restaurantInfo = await retrieveRestaurantBySlug(params);
            const [updatedRestaurantInfo, restaurantMenuResponse] = await Promise.all([
                retrieveRestaurantWithCalendar(restaurantInfo),
                retrieveRestaurantMenu(restaurantInfo),
            ]);
            restaurantInfo = updatedRestaurantInfo;
            restaurantMenu = restaurantMenuResponse;
        } else {
            restaurantMenu = await retrieveRestaurantMenu({ id: Number(genericRestaurantId) } as Restaurant);
        }

        const [dictionary] = await Promise.all([dictionaryPromise]);

        return {
            props: {
                params: {
                    ...params,
                    items: await GetNavigationItems(Config.Brand),
                    dictionary: dictionary,
                    restaurantMenu: restaurantMenu,
                    restaurantInfo: JSON.stringify(restaurantInfo),
                    auth0Config: Config.Auth0,
                },
            },
            revalidate: REVALIDATE_DELAY,
        };
    } catch {
        return {
            props: {
                params: {
                    ...params,
                    items: getEmptyNavigationItems(),
                    dictionary: null,
                    restaurantMenu: null,
                    restaurantInfo: null,
                    auth0Config: Config.Auth0,
                },
            },
            revalidate: REVALIDATE_DELAY,
        };
    }
}

const MenuPage = (props: BrowsableMenuPageProps): React.JSX.Element => {
    const router = useRouter();
    const publicUrl = Config.Urls.Cms;
    const params = props.params;

    const parsedRestaurantInfo: Restaurant | null = props.params.restaurantInfo
        ? (() => {
              try {
                  return JSON.parse(props.params.restaurantInfo);
              } catch (error) {
                  console.error('Failed to parse restaurantInfo:', error);
                  return null;
              }
          })()
        : null;
    if (params.restaurantSlug !== 'restaurant') {
        parsedRestaurantInfo?.calendars?.forEach((calendar) => {
            if (calendar.ranges.length > 0) {
                calendar.ranges[0].end = CreateIsoDate(calendar.ranges[0].end);
                calendar.ranges[0].start = CreateIsoDate(calendar.ranges[0].start);
            }
        });
    }

    const currentCategoryId =
        typeof router.query.categoryId === 'string' ? router.query.categoryId : props.params.categoryId;
    const selectedCategoryName = props.params.restaurantMenu?.categories?.find(
        (category) => category.id.toString() === currentCategoryId
    )?.name;
    const title = getMenuCategoryTitle(selectedCategoryName, 'Outback Steakhouse', parsedRestaurantInfo);

    const categoryId = Number(props.params.categoryId);
    const category = props.params.restaurantMenu?.categories?.find((c) => c.id === categoryId) ?? null;
    const description = buildCategoryMetaDescription(
        parsedRestaurantInfo,
        category,
        "Steaks, ribs, seafood, Bloomin' Onion & more",
        categoryId
    );

    const menuUrl = `${publicUrl}/menu/${props.params.restaurantSlug}`;
    const restaurantJsonLd = buildRestaurantMenuJsonLdForSlug(
        params.restaurantSlug ?? 'restaurant',
        parsedRestaurantInfo,
        menuUrl,
        'Steakhouse'
    );

    return (
        <ThemeProvider theme={OutbackTheme}>
            <ToastProvider>
                <Head>
                    <title>{title}</title>
                    <meta name='description' content={description} />
                    <link rel='icon' href={`${publicUrl}/favicon.ico`} />
                    {Number(props.params.categoryId) === 0 ? (
                        <link rel='canonical' href={`${publicUrl}/menu/${props.params.restaurantSlug}`} />
                    ) : (
                        <link
                            rel='canonical'
                            href={`${publicUrl}/menu/${props.params.restaurantSlug}/category/${props.params.categoryId}`}
                        />
                    )}
                    {restaurantJsonLd && (
                        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: restaurantJsonLd }} />
                    )}
                </Head>
                {GetHeader(params.items, params.auth0Config)}
                <div style={{ minHeight: 'calc(100vh - 100px)', backgroundColor: '#E5C8A2' }}>
                    <BrowsableMenu
                        router={router}
                        restaurantSlug={props.params.restaurantSlug}
                        restaurantMenu={props.params.restaurantMenu}
                        restaurantInfo={parsedRestaurantInfo}
                        categoryId={props.params.categoryId}
                    ></BrowsableMenu>
                </div>
                {GetFooter(params.items)}
            </ToastProvider>
        </ThemeProvider>
    );
};

export default MenuPage;
